// CSR-1960 — turns a DC record-update row into the labeled timeline card, so
// both the policy tab and the insured timeline render it identically.
import {
  formatBillingType,
  recordUpdateCommissionAmounts,
  recordUpdatePremiumIsAdded,
  type PolicyRecordUpdateRow,
  type RecordUpdateType,
} from "@shared/recordUpdate";
import { formatPolicyStatusLabel } from "@/components/insured/policies/policy-status";

const TYPE_LABELS: Record<RecordUpdateType, string> = {
  positive_endorsement: "Positive Endorsement",
  negative_endorsement: "Negative Endorsement",
  cancellation: "Cancellation",
  reinstatement: "Reinstatement",
};

// Accounting-style money: "$1,234.56", negatives in parentheses "($1,234.56)".
// Forced en-US so it never varies with the viewer's locale.
export function formatSignedMoney(value: string | number | null | undefined): string {
  const num = Number(value);
  if (value === null || value === undefined || value === "" || !Number.isFinite(num)) {
    return "$0.00";
  }
  const abs = Math.abs(num).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return num < 0 ? `($${abs})` : `$${abs}`;
}

function formatPercent(value: string | number | null | undefined): string {
  const num = Number(value);
  return Number.isFinite(num) ? String(num) : "0";
}

/** DC stores the effective date as YYYY-MM-DD; the card shows MM/DD/YYYY. */
function formatEffectiveDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return match ? `${match[2]}/${match[3]}/${match[1]}` : value;
}

export interface RecordUpdateCard {
  title: string;
  changeLines: string[];
  authorName: string;
}

export function buildRecordUpdateCard(row: PolicyRecordUpdateRow): RecordUpdateCard {
  const typeLabel = TYPE_LABELS[row.type] ?? row.type;
  const premiumMagnitude = Math.abs(Number(row.premium) || 0);
  const signedPremiumChange = recordUpdatePremiumIsAdded(row.type)
    ? premiumMagnitude
    : -premiumMagnitude;
  const commissions = recordUpdateCommissionAmounts(
    row.type,
    row.premium,
    row.agencyCommissionPct,
    row.agentCommissionPct,
  );

  const changeLines: string[] = [];
  const billingLabel = formatBillingType(row.billingType);
  if (billingLabel) changeLines.push(`Billing Type: ${billingLabel}`);
  const endorsementNumber = row.endorsementNumber?.trim();
  if (endorsementNumber) changeLines.push(`END #: ${endorsementNumber}`);
  changeLines.push(`Effective Date: ${formatEffectiveDate(row.effectiveDate)}`);
  changeLines.push(`Premium Change: ${formatSignedMoney(signedPremiumChange)}`);
  changeLines.push(`Broker Fees: ${formatSignedMoney(row.brokerFee)}`);
  changeLines.push(`MGA Fee: ${formatSignedMoney(row.mgaFee)}`);
  changeLines.push(`Taxes: ${formatSignedMoney(row.taxes)}`);
  changeLines.push(
    `Total Outstanding Premium: ${formatSignedMoney(row.totalOutstandingPremium)}`,
  );
  changeLines.push(`Total Non-Premium: ${formatSignedMoney(row.totalNonPremium)}`);
  changeLines.push(`Total: ${formatSignedMoney(row.resultingPremium)}`);
  changeLines.push(
    `Agency Commission (${formatPercent(row.agencyCommissionPct)}%): ${formatSignedMoney(commissions.agency)}`,
  );
  changeLines.push(
    `Agent Commission (${formatPercent(row.agentCommissionPct)}%): ${formatSignedMoney(commissions.agent)}`,
  );
  // Cancellation / reinstatement only — endorsements leave the status untouched.
  if (row.resultingStatus) {
    changeLines.push(`Policy Status: ${formatPolicyStatusLabel(row.resultingStatus)}`);
  }

  return {
    title: `${typeLabel} — Total ${formatSignedMoney(row.resultingPremium)}`,
    changeLines,
    authorName: row.authorDisplayName ?? "Unknown",
  };
}
